import React, { type JSX } from "react";
import {
  DocumentTextIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline";
import type {
  DocumentMetadata,
  MetadataFieldProps,
  Recipient,
  Sender,
} from "./yamlPreviewTypes";

/** Labelled metadata value with a leading icon. */
function MetadataField({ icon, label, value }: MetadataFieldProps): JSX.Element {
  return (
    <div className="flex items-start space-x-3">
      {icon}
      <div>
        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</div>
        <div className="text-sm text-gray-900 mt-0.5">{value}</div>
      </div>
    </div>
  );
}

const ICON_CLASS = "h-5 w-5 text-gray-400";

/** The four headline metadata fields, each shown only where populated. */
function MetadataGrid({ metadata }: { metadata: DocumentMetadata }): JSX.Element {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
      {metadata.letter_date && (
        <MetadataField
          icon={<CalendarIcon className={ICON_CLASS} />}
          label="Date"
          value={metadata.letter_date}
        />
      )}
      {metadata.target_company && (
        <MetadataField
          icon={<BuildingOfficeIcon className={ICON_CLASS} />}
          label="Target Company"
          value={metadata.target_company}
        />
      )}
      {metadata.valuation && (
        <MetadataField
          icon={<CurrencyDollarIcon className={ICON_CLASS} />}
          label="Valuation"
          value={metadata.valuation}
        />
      )}
      {metadata.recipient_bank && (
        <MetadataField
          icon={<BuildingOfficeIcon className={ICON_CLASS} />}
          label="Recipient Bank"
          value={metadata.recipient_bank}
        />
      )}
    </div>
  );
}

/** Recipients and senders share a name / role / organisation line. */
function PartyList({
  label,
  parties,
}: {
  label: string;
  parties: (Recipient | Sender)[];
}): JSX.Element {
  return (
    <div className="mt-4 pt-4 border-t border-gray-100">
      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{label}</div>
      <div className="space-y-1">
        {parties.map((party, idx) => (
          <div key={idx} className="text-sm text-gray-700">
            {party.name}
            {party.role && ` - ${party.role}`}
            {party.organisation && ` (${party.organisation})`}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Collapsible document metadata card. */
export default function YamlMetadataPanel({
  metadata,
  expanded,
  onToggle,
}: {
  metadata: DocumentMetadata;
  expanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-4">
      <div
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors cursor-pointer"
      >
        <div className="flex items-center space-x-2">
          <DocumentTextIcon className="h-5 w-5 text-cvc-blue" />
          <h2 className="text-base font-semibold text-gray-900">Document Metadata</h2>
          <span className="text-xs text-gray-500">({Object.keys(metadata).length} fields)</span>
        </div>
        {expanded ? (
          <ChevronDownIcon className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronRightIcon className="h-5 w-5 text-gray-400" />
        )}
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <MetadataGrid metadata={metadata} />
          {metadata.recipients && metadata.recipients.length > 0 && (
            <PartyList label="Recipients" parties={metadata.recipients} />
          )}
          {metadata.senders && metadata.senders.length > 0 && (
            <PartyList label="Senders" parties={metadata.senders} />
          )}
        </div>
      )}
    </div>
  );
}
